import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { adminUserService, UserDetails as UserDetailsType, Role, FamilyPlanForAdmin } from '@/services/adminUserService';
import { toastHelper } from '@/utils/toastHelper';
import { Plus, X } from 'lucide-react';

interface UserDetailRoleProps {
  userDetails: UserDetailsType;
  roles: Role[];
  onRefresh: () => void;
}

const UserDetailRole: React.FC<UserDetailRoleProps> = ({ userDetails, roles, onRefresh }) => {
  const [showAssignRoleDialog, setShowAssignRoleDialog] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState<string>('');
  const [selectedFamilyPlanId, setSelectedFamilyPlanId] = useState<string>('');
  const [familyPlans, setFamilyPlans] = useState<FamilyPlanForAdmin[]>([]);
  const [loadingFamilyPlans, setLoadingFamilyPlans] = useState(false);
  const [assigningRole, setAssigningRole] = useState(false);
  const [showRemoveRoleDialog, setShowRemoveRoleDialog] = useState(false);
  const [roleToRemove, setRoleToRemove] = useState<{ id: number; name: string } | null>(null);
  const [removingRole, setRemovingRole] = useState(false);

  // Filter out the base 'family' role and create virtual family options
  const familyRole = roles.find(r => r.name === 'family');
  const availableRoles = roles.filter(role => {
    // Don't show the base 'family' role
    if (role.name === 'family') {
      return false;
    }
    // Don't show roles the user already has (active)
    const userHasRole = userDetails.roles.some(
      ur => ur.id === role.id && ur.is_active
    );
    return !userHasRole;
  });

  // Check if user already has family role
  const userHasFamilyRole = userDetails.roles.some(
    ur => roles.find(r => r.id === ur.id && r.name === 'family')?.id && ur.is_active
  );

  // Fetch family plans when Family Member is selected
  useEffect(() => {
    if (selectedRoleId === 'family_member' && showAssignRoleDialog) {
      setLoadingFamilyPlans(true);
      adminUserService.getFamilyPlans()
        .then(setFamilyPlans)
        .catch(() => setFamilyPlans([]))
        .finally(() => setLoadingFamilyPlans(false));
    } else {
      setFamilyPlans([]);
      setSelectedFamilyPlanId('');
    }
  }, [selectedRoleId, showAssignRoleDialog]);

  const handleRoleChange = (value: string) => {
    setSelectedRoleId(value);
    setSelectedFamilyPlanId('');
  };

  const handleAssignRole = async () => {
    if (!userDetails.id || !selectedRoleId) return;
    
    try {
      setAssigningRole(true);
      const roleIdToAssign = selectedRoleId === 'family_owner' || selectedRoleId === 'family_member'
        ? familyRole?.id 
        : parseInt(selectedRoleId);
      
      if (!roleIdToAssign) {
        toastHelper.newMessage('error', 'Error', 'Family role not found');
        return;
      }

      const createFamilyPlan = selectedRoleId === 'family_owner';
      const familyPlanId = selectedRoleId === 'family_member' && selectedFamilyPlanId
        ? parseInt(selectedFamilyPlanId)
        : undefined;
      await adminUserService.assignRole(userDetails.id, roleIdToAssign, createFamilyPlan, familyPlanId);
      toastHelper.newMessage('success', 'Success', 'Role assigned successfully');
      setShowAssignRoleDialog(false);
      setSelectedRoleId('');
      setSelectedFamilyPlanId('');
      onRefresh();
    } catch (error: any) {
      toastHelper.newMessage('error', 'Error', error.response?.data?.message || 'Failed to assign role');
    } finally {
      setAssigningRole(false);
    }
  };

  const handleRemoveRoleClick = (roleId: number, roleName: string) => {
    setRoleToRemove({ id: roleId, name: roleName });
    setShowRemoveRoleDialog(true);
  };

  const handleRemoveRole = async () => {
    if (!userDetails.id || !roleToRemove) return;
    
    try {
      setRemovingRole(true);
      await adminUserService.removeRole(userDetails.id, roleToRemove.id);
      toastHelper.newMessage('success', 'Success', 'Role removed successfully');
      setShowRemoveRoleDialog(false);
      setRoleToRemove(null);
      onRefresh();
    } catch (error: any) {
      toastHelper.newMessage('error', 'Error', error.response?.data?.message || 'Failed to remove role');
    } finally {
      setRemovingRole(false);
    }
  };

  const activeRoles = userDetails.roles.filter(r => r.is_active);
  const inactiveRoles = userDetails.roles.filter(r => !r.is_active);

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Roles</CardTitle>
              <CardDescription>Manage user role assignments</CardDescription>
            </div>
            <Button
              onClick={() => setShowAssignRoleDialog(true)}
              size="sm"
              disabled={availableRoles.length === 0}
            >
              <Plus className="h-4 w-4 mr-2" />
              Assign Role
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {activeRoles.length > 0 ? (
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">Active Roles</h3>
                <div className="space-y-2">
                  {activeRoles.map((role) => (
                    <div
                      key={role.user_role_id}
                      className="flex items-center justify-between p-3 border rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        {role.icon && (
                          <img src={role.icon} alt={role.name} className="w-6 h-6" />
                        )}
                        <div>
                          <div className="font-medium">{role.name}</div>
                          {role.description && (
                            <div className="text-sm text-muted-foreground">
                              {role.description}
                            </div>
                          )}
                          <div className="text-xs text-muted-foreground mt-1">
                            Granted: {role.granted_at ? new Date(role.granted_at).toLocaleDateString() : 'N/A'}
                            {role.valid_until && (
                              <> • Expires: {new Date(role.valid_until).toLocaleDateString()}</>
                            )}
                          </div>
                        </div>
                      </div>
                      <Button
                        onClick={() => handleRemoveRoleClick(role.id, role.name)}
                        variant="ghost"
                        size="sm"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">No active roles assigned</p>
          )}

          {inactiveRoles.length > 0 && (
            <div className="mt-6">
              <h3 className="font-semibold mb-2 text-muted-foreground">Role Assignment History</h3>
              <div className="space-y-2">
                {inactiveRoles.map((role) => (
                  <div
                    key={role.user_role_id}
                    className="flex items-center justify-between p-3 border rounded-lg opacity-60"
                  >
                    <div className="flex items-center gap-3">
                      {role.icon && (
                        <img src={role.icon} alt={role.name} className="w-6 h-6" />
                      )}
                      <div>
                        <div className="font-medium">{role.name}</div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {role.granted_at ? `Granted: ${new Date(role.granted_at).toLocaleDateString()}` : 'Previously assigned'}
                          {role.valid_until && (
                            <> • Expired: {new Date(role.valid_until).toLocaleDateString()}</>
                          )}
                          {!role.valid_until && role.is_active === false && (
                            <> • Removed</>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Assign Role Dialog */}
      <Dialog
        open={showAssignRoleDialog}
        onOpenChange={(open) => {
          setShowAssignRoleDialog(open);
          if (!open) {
            setSelectedRoleId('');
            setSelectedFamilyPlanId('');
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Role</DialogTitle>
            <DialogDescription>
              Select a role to assign to this user
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Select value={selectedRoleId} onValueChange={handleRoleChange}>
                <SelectTrigger id="role">
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  {availableRoles.map((role) => (
                    <SelectItem key={role.id} value={role.id.toString()}>
                      {role.name}
                      {role.is_system_role && (
                        <span className="ml-2 text-xs text-muted-foreground">(System)</span>
                      )}
                    </SelectItem>
                  ))}
                  {familyRole && !userHasFamilyRole && (
                    <>
                      <SelectItem value="family_owner">
                        Family Owner
                        <span className="ml-2 text-xs text-muted-foreground">(Creates family plan)</span>
                      </SelectItem>
                      <SelectItem value="family_member">
                        Family Member
                        <span className="ml-2 text-xs text-muted-foreground">(Pro features only)</span>
                      </SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>

            {selectedRoleId === 'family_owner' && (
              <p className="text-sm text-muted-foreground rounded-md border p-3 bg-muted/50">
                This will create a new family plan record without a PayPal subscription. The user will be able to add and manage family members.
              </p>
            )}

            {selectedRoleId === 'family_member' && (
              <div className="space-y-2">
                <Label htmlFor="family-plan">Family Plan</Label>
                <Select
                  value={selectedFamilyPlanId}
                  onValueChange={setSelectedFamilyPlanId}
                  disabled={loadingFamilyPlans}
                >
                  <SelectTrigger id="family-plan">
                    <SelectValue placeholder={loadingFamilyPlans ? 'Loading...' : 'Select a family plan'} />
                  </SelectTrigger>
                  <SelectContent>
                    {familyPlans
                      .filter(fp => fp.member_count < fp.max_members)
                      .map((fp) => (
                        <SelectItem key={fp.id} value={fp.id.toString()}>
                          {fp.owner_email ?? 'Unknown'} (Sub: {fp.subscription_id})
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                {familyPlans.filter(fp => fp.member_count < fp.max_members).length === 0 && !loadingFamilyPlans && (
                  <p className="text-xs text-muted-foreground">No family plans with available slots</p>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowAssignRoleDialog(false);
                setSelectedRoleId('');
                setSelectedFamilyPlanId('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAssignRole}
              disabled={
                !selectedRoleId ||
                assigningRole ||
                (selectedRoleId === 'family_member' && !selectedFamilyPlanId)
              }
            >
              {assigningRole ? 'Assigning...' : 'Assign Role'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove Role Confirmation Dialog */}
      <Dialog open={showRemoveRoleDialog} onOpenChange={setShowRemoveRoleDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Role</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove the <strong>{roleToRemove?.name}</strong> role from this user?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowRemoveRoleDialog(false);
                setRoleToRemove(null);
              }}
              disabled={removingRole}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleRemoveRole}
              disabled={removingRole}
            >
              {removingRole ? 'Removing...' : 'Remove Role'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default UserDetailRole;

